import React, { useState } from "react";
import { ChevronDown, Eye, ShieldCheck, TriangleAlert } from "lucide-react";

/**
 * Scene & Background Forensics — what the captured images say about the
 * circumstances of the shoot, as opposed to the damage itself.
 *
 * Sits under the 3D model on the damage report. Fed by the `forensics` block of
 * ../poiTracking.js.
 *
 * Deliberately inverted from the payload: /poi_tracking returns the same
 * assessment for every image, and eight near-identical cards would bury the one
 * thing a reviewer needs — where the images *disagree*, and which checks were
 * actually raised. Agreement is stated once; disagreement and raised flags are
 * called out.
 */

const card = "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
const cardHead =
  "flex items-center justify-between gap-3 border-b border-cl-head px-4 py-3";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
const label =
  "text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label";

const pretty = (value) =>
  typeof value === "string"
    ? value.replace(/[-_]+/g, " ").replace(/^./, (c) => c.toUpperCase())
    : value;

export default function SceneForensics({ forensics }) {
  const [expanded, setExpanded] = useState(false);

  if (!forensics) return null;

  const raised = forensics.flags.filter((f) => f.raised);
  const notable = forensics.perImage.filter(
    (img) =>
      img.raisedFlags.length ||
      img.obstructions.length ||
      img.issues.length ||
      img.lightingIssue ||
      img.underRepair ||
      img.dismantled,
  );

  return (
    <div className={card} id="scene-forensics">
      <div className={cardHead}>
        <div className="flex items-center gap-2">
          <Eye className="h-[15px] w-[15px] text-cl-accent" />
          <span className={cardTitle}>Scene &amp; background forensics</span>
        </div>
        <span className="text-[11px] text-cl-faint">
          {forensics.imageCount} image
          {forensics.imageCount === 1 ? "" : "s"} analysed
        </span>
      </div>

      {/* Agreed conditions, stated once */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-[13px] px-4 py-[15px] sm:grid-cols-3 lg:grid-cols-5">
        {forensics.consensus
          .filter((item) => item.values.length > 0)
          .map((item) => (
            <div key={item.label} className="min-w-0">
              <div className={label}>{item.label}</div>
              {item.mixed ? (
                <div
                  className="truncate text-[12.5px] font-medium text-[#9a6212]"
                  title={item.values.map(pretty).join(", ")}
                >
                  {/* Mixed is a signal, not a gap: name the values. */}
                  Mixed · {item.values.map(pretty).join(", ")}
                </div>
              ) : (
                <div className="truncate text-[12.5px] font-medium text-cl-text">
                  {pretty(item.value)}
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Identified vehicle across the set */}
      {forensics.identity && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-cl-head px-4 py-[11px]">
          <div className="min-w-0">
            <div className={label}>Vehicle in frame</div>
            <div className="text-[12.5px] font-medium text-cl-text">
              {[forensics.identity.make, forensics.identity.model]
                .filter(Boolean)
                .join(" ") || "—"}
              {forensics.identity.numberPlate
                ? ` · ${forensics.identity.numberPlate}`
                : ""}
            </div>
          </div>
          <div
            className="flex items-center gap-[6px] text-[12px]"
            style={{
              color: forensics.identity.consistent ? "#1c7a43" : "#c22f2f",
            }}
          >
            {forensics.identity.consistent ? (
              <>
                <ShieldCheck className="h-[14px] w-[14px]" />
                Same vehicle across all images
              </>
            ) : (
              <>
                <TriangleAlert className="h-[14px] w-[14px]" />
                {forensics.identity.inconsistentCount} image
                {forensics.identity.inconsistentCount === 1 ? "" : "s"} may show a
                different vehicle
              </>
            )}
          </div>
        </div>
      )}

      {/* Validation checks — raised first, the quiet ones still listed so their
          absence is visible rather than merely unmentioned. */}
      <div className="border-t border-cl-head px-4 py-[13px]">
        <div className={label}>Validation checks</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {forensics.flags.map((flag) => (
            <span
              key={flag.key}
              title={
                flag.raised
                  ? `Raised in: ${flag.raisedBy.join(", ")}`
                  : "Not detected in any image"
              }
              className="inline-flex items-center gap-[6px] rounded-full border px-[9px] py-[4px] text-[11.5px]"
              style={
                flag.raised
                  ? {
                      color: "#9a6212",
                      borderColor: "rgba(154,98,18,0.35)",
                      background: "rgba(154,98,18,0.08)",
                    }
                  : {
                      color: "var(--cl-text-faint)",
                      borderColor: "var(--cl-border-control)",
                      background: "transparent",
                    }
              }
            >
              {flag.raised ? (
                <TriangleAlert className="h-[12px] w-[12px] shrink-0" />
              ) : (
                <ShieldCheck className="h-[12px] w-[12px] shrink-0" />
              )}
              {flag.label}
              {flag.raised && flag.raisedBy.length > 1 && (
                <span className="font-semibold">×{flag.raisedBy.length}</span>
              )}
            </span>
          ))}
        </div>

        {forensics.obstructions.length > 0 && (
          <div className="mt-3">
            <div className={label}>Obstructions</div>
            <ul className="mt-1 list-disc space-y-[2px] pl-5 text-[12px] text-cl-secondary">
              {forensics.obstructions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {forensics.findings.length > 0 && (
          <div className="mt-3">
            <div className={label}>Findings</div>
            <ul className="mt-1 list-disc space-y-[2px] pl-5 text-[12px] text-cl-secondary">
              {forensics.findings.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Per-image detail, collapsed by default */}
      {forensics.perImage.length > 0 && (
        <div className="border-t border-cl-head">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="flex w-full items-center justify-between gap-2 px-4 py-[11px] text-left transition-colors hover:bg-cl-hover"
          >
            <span className="text-[12.5px] font-medium text-cl-text">
              Per-image detail
              {notable.length > 0 && (
                <span className="ml-2 text-[11.5px] font-normal text-cl-muted">
                  {notable.length} with something noted
                </span>
              )}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-cl-muted transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </button>

          {expanded && (
            <div className="divide-y divide-cl-row border-t border-cl-row">
              {forensics.perImage.map((img) => (
                <div key={img.id} className="px-4 py-[11px]">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-[12.5px] font-semibold text-cl-text">
                      {pretty(img.viewSide) || img.filename}
                    </span>
                    {img.scene && (
                      <span className="text-[11.5px] text-cl-muted">
                        {pretty(img.scene)}
                      </span>
                    )}
                    {img.quality && (
                      <span className="text-[11.5px] text-cl-faint">
                        quality: {pretty(img.quality)}
                        {img.blur ? ` · blur: ${pretty(img.blur)}` : ""}
                      </span>
                    )}
                  </div>

                  {img.locationFinding && (
                    <p className="mt-1 text-[12px] leading-[1.5] text-cl-secondary">
                      {img.locationFinding}
                    </p>
                  )}

                  {(img.raisedFlags.length > 0 ||
                    img.obstructions.length > 0 ||
                    img.issues.length > 0 ||
                    img.lightingIssue ||
                    img.underRepair ||
                    img.dismantled) && (
                    <div className="mt-[6px] flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-[#9a6212]">
                      {img.raisedFlags.map((f) => (
                        <span key={f}>▲ {f}</span>
                      ))}
                      {img.obstructions.map((o) => (
                        <span key={o}>▲ {o}</span>
                      ))}
                      {img.issues.map((i) => (
                        <span key={i}>▲ {pretty(i)}</span>
                      ))}
                      {img.lightingIssue && <span>▲ Lighting issue</span>}
                      {img.underRepair && <span>▲ Under repair</span>}
                      {img.dismantled && <span>▲ Dismantled</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
